import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { logout } from '../utils/auth';
import { supabase } from '../config/supabase';
import {
  LayoutDashboard,
  User,
  LogOut,
  BarChart2,
  Building,
  Landmark,
  CreditCard,
  Receipt,
  Wallet2,
  CalendarDays,
} from 'lucide-react';

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [userData, setUserData] = useState<any>(null);

  const navigationItems = [
    { name: 'Dashboard',        icon: LayoutDashboard, path: '/home' },
    { name: 'My Bank Accounts', icon: Landmark,        path: '/bank-accounts' },
    { name: 'My Debts',         icon: Building,        path: '/my-debts' },
    { name: 'Subscriptions',    icon: Receipt,         path: '/subscriptions' },
    { name: 'Spending',         icon: Wallet2,         path: '/spending' },
    { name: 'Monthly Forecast', icon: CalendarDays,    path: '/forecast' },
    { name: 'Analytics',        icon: BarChart2,       path: '/analytics' },
  ];

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) {
        setUserData({ email: user.email, role: 'User' });
      }
    });
  }, []);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    await logout();
    setIsLoggingOut(false);
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="hidden lg:flex h-screen">
      {/* Sidebar — desktop only */}
      <div className="fixed inset-y-0 left-0 z-50 w-64 flex flex-col lg:static lg:inset-0"
        style={{ background: 'linear-gradient(180deg, #13131f 0%, #0f0f19 100%)', borderRight: '1px solid rgba(255,255,255,0.06)' }}>

        {/* Logo */}
        <div className="flex items-center h-16 px-5 border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-indigo-600">
              <CreditCard className="h-4 w-4 text-white" />
            </div>
            <span className="text-lg font-bold gradient-text">My Finance</span>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-5 space-y-1">
          {navigationItems.map((item) => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                isActive(item.path)
                  ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/25'
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <item.icon className="h-4 w-4 flex-shrink-0" />
              {item.name}
            </button>
          ))}
        </nav>

        {/* User */}
        <div className="p-4 border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center flex-shrink-0">
              <User className="h-4 w-4 text-indigo-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{userData?.email || 'User'}</p>
              <p className="text-xs text-gray-500 capitalize">{userData?.role || ''}</p>
            </div>
            <button
              onClick={handleLogout}
              disabled={isLoggingOut}
              className="text-gray-500 hover:text-red-400 transition-colors p-1 rounded-lg hover:bg-red-400/10 flex-shrink-0"
              title="Logout"
            >
              {isLoggingOut
                ? <div className="animate-spin h-4 w-4 border-2 border-indigo-400 border-t-transparent rounded-full" />
                : <LogOut className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
